import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useSelector } from 'react-redux';

import { useSecondaryPanel } from '@src/components/SecondaryPanel/SecondaryPanel';
import type { CommonReduxState } from '@src/models/commons/reduxState.interface';
import {
  WORK_JOURNEY_LIST_FILTERS_STORAGE_KEY,
  WORK_JOURNEY_LIST_PAGE_SIZE,
  WORK_JOURNEY_LIST_SEARCH_IDS_SIZE,
  WORK_JOURNEY_LIST_SORT_STORAGE_KEY,
  WORK_JOURNEY_LIST_VIEW_STORAGE_KEY,
} from '@src/packages/workJourney/constants';
import { useWorkJourneyContext } from '@src/packages/workJourney/contexts';
import type { WorkJourneyForm } from '@src/packages/workJourney/types';
import {
  filterValuesToWorkJourneyFilters,
  INIT_WORK_JOURNEY_LIST_FILTERS,
} from '@src/packages/workJourney/workJourneyListFilterConfig';
import {
  isFilterPanelHydrated,
  useFirestoreRealtimeBatchedPagination,
  usePaginationWithFirestore,
  usePersistedFilters,
} from '@src/packages/utils';
import { migrateLegacyListViewState } from '@src/packages/utils/sessionStorage/viewState';
import WorkJourneyService from '@src/services/WorkJourneyService';

import { buildWorkJourneyQueries } from '../utils/buildWorkJourneyQueries';
import {
  hasPersistedBootstrapWorkJourneyFilters,
  hasTextWorkJourneyFilters,
  shouldCompletePersistedListFiltersBootstrap,
  shouldUsePersistedListFiltersBootstrap,
  intersectWorkJourneyConfigIds,
} from '../utils/workJourneyListFilterHelpers';
import { useWorkJourneyConfigSubcollections } from './useWorkJourneyConfigSubcollections';

const resolveElasticSearchIds = async ({
  orgId,
  journeySearchTerm,
  userIds,
  workJourneyService,
}: {
  orgId: string;
  journeySearchTerm: string;
  userIds: string[];
  workJourneyService: WorkJourneyService;
}): Promise<string[]> => {
  const hasJourneySearch = Boolean(journeySearchTerm);
  const hasUserFilter = userIds.length > 0;

  if (!hasJourneySearch && !hasUserFilter) {
    return [];
  }

  let journeyIds: string[] = [];
  if (hasJourneySearch) {
    const journeyResult = await workJourneyService.searchIds({
      orgId,
      pageSize: WORK_JOURNEY_LIST_SEARCH_IDS_SIZE,
      from: 0,
      searchTerm: journeySearchTerm,
    });
    journeyIds = journeyResult?.ids ?? [];
  }

  let memberConfigIds: string[] = [];
  if (hasUserFilter) {
    memberConfigIds = await workJourneyService.listConfigIdsByMemberUserIds(orgId, userIds);
  }

  if (hasJourneySearch && hasUserFilter) {
    return intersectWorkJourneyConfigIds(journeyIds, memberConfigIds);
  }

  return hasJourneySearch ? journeyIds : memberConfigIds;
};

export const useWorkJourneyList = () => {
  const orgId = useSelector(
    ({ organizations }: CommonReduxState) => organizations.organization.id,
  );
  const { refreshTrigger, setRefreshTrigger } = useWorkJourneyContext();

  useState(() => {
    migrateLegacyListViewState(WORK_JOURNEY_LIST_VIEW_STORAGE_KEY, {
      filters: WORK_JOURNEY_LIST_FILTERS_STORAGE_KEY,
      sort: WORK_JOURNEY_LIST_SORT_STORAGE_KEY,
    });
    return true;
  });

  const {
    values: filters,
    debouncedValues,
    setValue: setFilters,
    toggle,
    isOpen,
  } = useSecondaryPanel({ debounceDelay: 500 });

  const { initialFilters } = usePersistedFilters({
    viewStorageKey: WORK_JOURNEY_LIST_VIEW_STORAGE_KEY,
    defaults: INIT_WORK_JOURNEY_LIST_FILTERS,
    values: filters,
  });

  const persistedListFilters = useMemo(
    () => filterValuesToWorkJourneyFilters(initialFilters),
    [initialFilters],
  );

  const debouncedListFilters = useMemo(
    () => filterValuesToWorkJourneyFilters(debouncedValues),
    [debouncedValues],
  );

  const currentListFilters = useMemo(
    () => filterValuesToWorkJourneyFilters(filters),
    [filters],
  );

  const [isPersistedBootstrapComplete, setIsPersistedBootstrapComplete] = useState(() =>
    !hasPersistedBootstrapWorkJourneyFilters(persistedListFilters),
  );

  useEffect(() => {
    if (isPersistedBootstrapComplete) {
      return;
    }

    if (
      shouldCompletePersistedListFiltersBootstrap(
        debouncedListFilters,
        persistedListFilters,
        currentListFilters,
      )
    ) {
      setIsPersistedBootstrapComplete(true);
    }
  }, [
    isPersistedBootstrapComplete,
    debouncedListFilters,
    persistedListFilters,
    currentListFilters,
  ]);

  const shouldUsePersistedBootstrap = shouldUsePersistedListFiltersBootstrap(
    debouncedListFilters,
    persistedListFilters,
    isPersistedBootstrapComplete,
  );

  // Until the panel hydrates, the mount snapshot is the only meaningful filter
  // set: switching to the empty panel values would look like a filter change and
  // would drop the restored page.
  const listFilters =
    shouldUsePersistedBootstrap || !isFilterPanelHydrated(debouncedValues)
      ? persistedListFilters
      : debouncedListFilters;

  const [searchIds, setSearchIds] = useState<string[] | null>(null);
  const [searchLoading, setSearchLoading] = useState(() =>
    hasTextWorkJourneyFilters(persistedListFilters),
  );
  const [searchError, setSearchError] = useState<unknown>(null);
  const [searchNonce, setSearchNonce] = useState(0);

  const workJourneyServiceRef = useRef<WorkJourneyService | null>(null);
  if (workJourneyServiceRef.current === null) {
    workJourneyServiceRef.current = new WorkJourneyService();
  }

  const journeySearchTerm = listFilters.search.trim();
  const userIds = listFilters.userIds;
  const hasElasticSearchFilters = hasTextWorkJourneyFilters({
    search: journeySearchTerm,
    userIds,
  });

  const isElasticResolutionPending =
    hasElasticSearchFilters && (searchLoading || searchIds == null);

  const refetchSearchIds = useCallback(() => {
    setSearchNonce(nonce => nonce + 1);
  }, []);

  useEffect(() => {
    refetchSearchIds();
  }, [refreshTrigger, refetchSearchIds]);

  useEffect(() => {
    if (!hasElasticSearchFilters) {
      setSearchIds(null);
      setSearchLoading(false);
      setSearchError(null);
      return undefined;
    }

    if (!orgId) {
      setSearchIds([]);
      setSearchLoading(false);
      setSearchError(null);
      return undefined;
    }

    let cancelled = false;
    setSearchLoading(true);
    setSearchIds(null);
    setSearchError(null);

    (async () => {
      try {
        const ids = await resolveElasticSearchIds({
          orgId,
          journeySearchTerm,
          userIds,
          workJourneyService: workJourneyServiceRef.current as WorkJourneyService,
        });
        if (cancelled) return;
        setSearchIds(ids);
      } catch (error) {
        if (cancelled) return;
        setSearchIds([]);
        setSearchError(error);
      } finally {
        if (!cancelled) {
          setSearchLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [orgId, journeySearchTerm, userIds, hasElasticSearchFilters, searchNonce]);

  const queriesList = useMemo(() => {
    if (!orgId) {
      return [];
    }
    if (hasElasticSearchFilters && (searchLoading || searchIds == null)) {
      return [];
    }
    return buildWorkJourneyQueries(
      orgId,
      hasElasticSearchFilters ? searchIds : null,
      listFilters,
    );
  }, [orgId, hasElasticSearchFilters, searchIds, searchLoading, listFilters]);

  const {
    data: realtimeJourneys,
    loading: realtimeLoading,
    error: realtimeError,
    next,
    gotNewData,
  } = useFirestoreRealtimeBatchedPagination(
    queriesList,
    [
      orgId,
      searchIds,
      hasElasticSearchFilters,
      searchNonce,
      listFilters.shift,
      listFilters.state,
      listFilters.dateRange,
      journeySearchTerm,
      userIds.join(','),
    ],
    WORK_JOURNEY_LIST_PAGE_SIZE,
  );

  const parentDocs = useMemo(
    () => (realtimeJourneys as WorkJourneyForm[]).map(item => ({ ...item })),
    [realtimeJourneys],
  );

  const journeysWithSubcollections = useWorkJourneyConfigSubcollections(orgId, parentDocs);

  const journeys = useMemo(() => {
    if (!hasElasticSearchFilters) {
      return journeysWithSubcollections;
    }

    if (!searchIds?.length) {
      return [];
    }

    const byId = new Map(journeysWithSubcollections.map(item => [item.id, item] as const));

    return searchIds
      .map(id => byId.get(id))
      .filter((journey): journey is WorkJourneyForm => journey != null);
  }, [hasElasticSearchFilters, journeysWithSubcollections, searchIds]);

  const hasMoreFirestoreData = hasElasticSearchFilters
    ? Boolean(gotNewData && searchIds != null && journeys.length < searchIds.length)
    : Boolean(gotNewData);

  const isFetching = isElasticResolutionPending ? true : realtimeLoading;

  const paginationState = usePaginationWithFirestore(
    journeys,
    { gotNewData: hasMoreFirestoreData, loading: isFetching, next },
    WORK_JOURNEY_LIST_PAGE_SIZE,
    listFilters,
    { fillIncompletePage: true, viewStorageKey: WORK_JOURNEY_LIST_VIEW_STORAGE_KEY },
  );

  const error = searchError ?? realtimeError;

  // Restoring the stored page is part of the first load: the view already sits
  // on that page with no rows, so it must show loading instead of the page it is
  // about to leave (see single-loading-indicator.mdc).
  const isLoading = isFetching || paginationState.isRestoringPage;

  const paginatedData = isElasticResolutionPending
    ? []
    : (paginationState.paginatedData as WorkJourneyForm[]);

  return {
    filters,
    initialFilters,
    setFilters,
    toggle,
    isOpen,
    journeys,
    paginatedData,
    paginationState: {
      currentPage: paginationState.currentPage,
      pageSize: paginationState.pageSize,
      estimatedTotal: paginationState.estimatedTotal,
      totalItems: paginationState.totalItems,
      handlePageChange: paginationState.handlePageChange,
      loading: isLoading,
      hasMoreData: paginationState.hasMoreData,
    },
    loading: isLoading,
    error,
    refetchSearchIds,
    setRefreshTrigger,
  };
};
